import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { paths } from '@/paths'
import { FeatureNames } from '@/lib/featureFlags'
import useTranslate from '@/hooks/use-translate'
import { useCommandBarDeclaration } from '../context/CommandBarDeclarationContext'
import {
  COMMAND_BAR_ACTIONS,
  hasDeclarationHandler,
} from '../commandBarActions'
import type { AddRecentPayload } from './useCommandBarRecents'
import type { CommandBarActionId, CommandBarItem, CommandBarModalId } from '../types'
import { useFeatureFlags } from '@/hooks/use-feature-flags'

type UseCommandBarExecuteArgs = {
  close: () => void
  openModal: (modalId: CommandBarModalId) => void
  addRecent: (entry: AddRecentPayload) => void
}

export function useCommandBarExecute({
  close,
  openModal,
  addRecent,
}: UseCommandBarExecuteArgs) {
  const navigate = useNavigate()
  const { isFeatureEnabled } = useFeatureFlags()
  const { handlers } = useCommandBarDeclaration()
  const { t } = useTranslate('commandBar')

  /** L'écran a-t-il le geste de l'action ? Maj + lettre le demande avant de prendre la touche. */
  const canExecuteAction = useCallback(
    (actionId: CommandBarActionId) =>
      hasDeclarationHandler(
        COMMAND_BAR_ACTIONS.find(({ id }) => id === actionId),
        handlers
      ),
    [handlers]
  )

  const executeAction = useCallback(
    (actionId: CommandBarActionId) => {
      // Action de déclaration : elle lance le geste que l'écran a enregistré (`handler`), sinon rien.
      const action = COMMAND_BAR_ACTIONS.find(({ id }) => id === actionId)
      if (action?.handler) {
        const runHandler = handlers?.[action.handler]
        if (!runHandler) return
        runHandler()
        // Seules les actions hors « Sur la déclaration » vont aux récents, comme avant.
        if (!action.isDeclarationContextual) {
          addRecent({
            id: actionId,
            type: 'action',
            label: t(action.labelKey),
            actionId,
          })
        }
        close()
        return
      }

      switch (actionId) {
        case 'create-folder':
          openModal('create-folder')
          addRecent({
            id: actionId,
            type: 'action',
            label: t('actionCreateFolder'),
            actionId,
          })
          close()
          return
        case 'add-referential':
          openModal('add-referential')
          addRecent({
            id: actionId,
            type: 'action',
            label: t('actionAddReferential'),
            actionId,
          })
          close()
          return
        case 'add-client':
          openModal('add-client')
          addRecent({
            id: actionId,
            type: 'action',
            label: t('actionAddClient'),
            actionId,
          })
          close()
          return
        case 'create-automation': {
          navigate(paths.dashboard.customerFlow.referentials)
          addRecent({
            id: actionId,
            type: 'action',
            label: t('actionCreateAutomation'),
            actionId,
            path: paths.dashboard.customerFlow.referentials,
          })
          close()
          return
        }
        case 'go-dashboard':
          navigate(paths.dashboard.index)
          addRecent({
            id: actionId,
            type: 'action',
            label: t('actionGoDashboard'),
            actionId,
            path: paths.dashboard.index,
          })
          close()
          return
        case 'view-all-documents':
          navigate(paths.dashboard.documents.list)
          addRecent({
            id: actionId,
            type: 'action',
            label: t('actionViewAllDocuments'),
            actionId,
            path: paths.dashboard.documents.list,
          })
          close()
          return
        case 'open-settings': {
          const useNewSettings = isFeatureEnabled(FeatureNames.newSettings)
          const settingsPath = useNewSettings
            ? paths.dashboard.settings.index
            : paths.dashboard.managementConsole.index
          navigate(settingsPath)
          addRecent({
            id: actionId,
            type: 'action',
            label: t('actionOpenSettings'),
            actionId,
            path: settingsPath,
          })
          close()
          return
        }
        default:
          return
      }
    },
    [addRecent, close, handlers, isFeatureEnabled, navigate, openModal, t]
  )

  const executeItem = useCallback(
    (item: CommandBarItem) => {
      if (item.disabled) return

      if (item.actionId) {
        executeAction(item.actionId)
        return
      }

      if (item.path) {
        navigate(item.path)
        addRecent({
          id: item.entityId ?? item.id,
          type: item.type === 'recent' ? 'document' : item.type,
          label: item.label,
          subtitle: item.subtitle,
          path: item.path,
        })
        close()
      }
    },
    [addRecent, close, executeAction, navigate]
  )

  return { executeItem, executeAction, canExecuteAction }
}
